// Section review finalisation
// ----------------------------
// Single, shared implementation of the "Save & Exit" write that persists a
// report section's approval flag (ciReportApproved / ssiReportApproved /
// ddReportApproved / padReportApproved).
//
// Why this exists (clinical-safety context):
// Each Review Document page (RD_*.js) used to build this write inline, deriving
// "fully approved" from React state (the optimistic in-session Set) and writing
// it inside a transaction that read the document but ignored the read. That let
// the persisted flag diverge from Firestore: a fast approve-then-exit could
// persist the wrong value, and the flag is trusted downstream to trigger the
// section render and the Patient Report. The result was Patient Reports being
// finalised while paragraphs were still unapproved.
//
// This helper closes that gap by DERIVING the flag inside the transaction from
// the freshly-read document (its outputs + approvedParagraphs), using the exact
// same computeApprovalState logic that drives the on-screen progress. The value
// we persist is therefore, by construction, consistent with Firestore at commit
// time — never a stale client guess.
//
// Callers must settle any in-flight per-paragraph approval writes (see
// usePendingApprovals) BEFORE calling this, so the fresh read includes them.

import { db, auth } from '../firebase';
import { doc, runTransaction, serverTimestamp } from 'firebase/firestore';
import { buildFirstApprovedFieldsDotNotation } from './reportAuditHelpers';
import { computeApprovalState } from './approvalStateUtils';
import { getPatientReportContentPathSegments } from '../constants/adultAutismFirestorePaths';

/**
 * Finalise a report section's approval state transactionally.
 *
 * @param {Object} params
 * @param {string} params.patientId     Firestore patient document ID
 * @param {string} params.modulePrefix  Report prefix: 'ci' | 'ssi' | 'dd' | 'pad'
 * @param {number|string} params.moduleNumber  manageReports section key (1..4)
 * @param {Object} params.docStructure  The page's docStructure (section -> { code })
 * @param {Set<string>} [params.excludedCodes]  Codes deleted this session (e.g. strengths)
 * @returns {Promise<{
 *   isFullyApproved: boolean,
 *   outstandingCodes: string[],
 *   manageReports: Object,
 * }>} The derived approval result plus the pre-update manageReports snapshot
 *     (so callers can compute first-occurrence audit flags).
 */
export const finalizeSectionReview = async ({
  patientId,
  modulePrefix,
  moduleNumber,
  docStructure,
  excludedCodes,
}) => {
  // Populated inside the transaction. runTransaction may retry the callback on
  // contention; the last successful run's values win, which is what we want.
  let result = { isFullyApproved: false, outstandingCodes: [], manageReports: {} };

  await runTransaction(db, async (transaction) => {
    const docRef = doc(db, 'patients', patientId);
    const docSnap = await transaction.get(docRef);

    if (!docSnap.exists()) {
      throw new Error('Document does not exist!');
    }

    const data = docSnap.data();
    const manageReports = data?.dataModel?.['6_manageReports'] || {};
    const section = manageReports?.sections?.[`${moduleNumber}`] || {};
    const outputs = section?.outputs || {};
    const approvedParagraphs = section?.approvedParagraphs || {};

    // Derive the flag from the SNAPSHOT — not from React state — so the
    // persisted value cannot disagree with the document it is written into.
    const { isFullyApproved, outstandingCodes } = computeApprovalState({
      docStructure,
      outputs,
      approvedParagraphs,
      excludedCodes,
    });

    const user = auth.currentUser;
    const updates = {
      [`dataModel.6_manageReports.${modulePrefix}ReportApproved`]: isFullyApproved,
      [`dataModel.6_manageReports.${modulePrefix}ReportApprovedBy`]: isFullyApproved ? user?.uid ?? null : null,
      [`dataModel.6_manageReports.${modulePrefix}ReportApprovedByName`]: isFullyApproved ? user?.displayName ?? null : null,
      [`dataModel.6_manageReports.${modulePrefix}ReportApprovedAt`]: isFullyApproved ? serverTimestamp() : null,
      // First-occurrence timestamp is derived from the SNAPSHOT manageReports so
      // it is written once, correctly, regardless of stale React-context data.
      ...(isFullyApproved ? buildFirstApprovedFieldsDotNotation(modulePrefix, manageReports) : {}),
    };

    transaction.update(docRef, updates);

    result = {
      isFullyApproved,
      outstandingCodes: [...outstandingCodes],
      manageReports,
    };
  });

  return result;
};

/**
 * Autism counterpart of finalizeSectionReview.
 *
 * Why a separate helper (rather than parameterising finalizeSectionReview):
 * the Autism data model is materially different. Each stage stores its
 * outputs / approvedParagraphs / approval flags as FLAT top-level fields on a
 * dedicated reportContent/{stageKey} subcollection document — not nested under
 * dataModel.6_manageReports.sections.N on the monolithic patient doc. The doc
 * reference, the field paths, and the first-approval field shape (Autism writes
 * three fields: FirstApprovedAt / By / ByName; ADHD writes only the timestamp)
 * all differ. Sharing the same computeApprovalState keeps the "is this section
 * fully approved?" logic identical across both products, which is the part that
 * actually matters for correctness; the persistence shell is intentionally
 * product-specific.
 *
 * The flag we persist is DERIVED FROM THE FRESH SNAPSHOT inside the transaction
 * (outputs + approvedParagraphs), never from the client's optimistic React
 * state, so it cannot disagree with the document it is written into. This is the
 * same clinical-safety guarantee as the ADHD helper.
 *
 * It also fixes a latent write-once bug the Autism pages carried inline: the
 * first-approval fields used to be gated on possibly-stale React context
 * (historyDoc?.historyReportFirstApprovedAt). Here we gate them on the freshly
 * read snapshot instead, so the "first ever approval" audit stamp is written
 * exactly once and correctly.
 *
 * Callers must settle any in-flight per-paragraph approval writes (see
 * usePendingApprovals) BEFORE calling this, so the fresh read includes them.
 *
 * @param {Object} params
 * @param {string} params.patientId    Firestore patient document ID
 * @param {string} params.stageKey     reportContent subcollection doc key
 *                                      ('history' | 'semiStructuredInterview' |
 *                                      'diagnosticDecision' | 'postAssessmentDiscussion' |
 *                                      'observation')
 * @param {string} params.flagPrefix   Approval flag prefix ('history' | 'ssi' |
 *                                      'dd' | 'pad' | 'obs'). Distinct from
 *                                      stageKey (e.g. stage 'observation' -> 'obs').
 * @param {Object} params.docStructure The page's docStructure (section -> { code })
 * @param {Set<string>} [params.excludedCodes]  Codes to treat as not-present
 * @returns {Promise<{
 *   isFullyApproved: boolean,
 *   outstandingCodes: string[],
 *   wasFirstApproval: boolean,
 * }>} The derived approval result. wasFirstApproval is true only when this exit
 *     is the first time the section reached full approval (used to stamp the
 *     first-occurrence audit entry from transactional truth).
 */
export const finalizeModularSectionReview = async ({
  patientId,
  stageKey,
  flagPrefix,
  docStructure,
  excludedCodes,
  // Path builder for the reportContent doc. Defaults to the Adult Autism
  // subcollection so every existing caller is unchanged; the CYP ADHD review
  // pages pass getCypAdhdReportContentPathSegments to target
  // patients/{id}/cyp_adhd/... instead.
  getPathSegments = getPatientReportContentPathSegments,
}) => {
  let result = { isFullyApproved: false, outstandingCodes: [], wasFirstApproval: false };

  await runTransaction(db, async (transaction) => {
    const docRef = doc(db, ...getPathSegments(patientId, stageKey));
    const docSnap = await transaction.get(docRef);

    if (!docSnap.exists()) {
      throw new Error('Report content document does not exist!');
    }

    const data = docSnap.data();
    const outputs = data?.outputs || {};
    const approvedParagraphs = data?.approvedParagraphs || {};

    // Derive the flag from the SNAPSHOT — not from React state — so the
    // persisted value cannot disagree with the document it is written into.
    const { isFullyApproved, outstandingCodes } = computeApprovalState({
      docStructure,
      outputs,
      approvedParagraphs,
      excludedCodes,
    });

    const user = auth.currentUser;

    // First-approval is derived from the fresh snapshot, not stale context, so
    // the write-once stamp lands exactly once even across rapid re-approvals.
    const firstApprovedKey = `${flagPrefix}ReportFirstApprovedAt`;
    const wasFirstApproval = isFullyApproved && !data?.[firstApprovedKey];

    const updates = {
      [`${flagPrefix}ReportApproved`]: isFullyApproved,
      [`${flagPrefix}ReportApprovedBy`]: isFullyApproved ? user?.uid ?? null : null,
      [`${flagPrefix}ReportApprovedByName`]: isFullyApproved ? user?.displayName ?? null : null,
      [`${flagPrefix}ReportApprovedAt`]: isFullyApproved ? serverTimestamp() : null,
    };

    if (wasFirstApproval) {
      updates[firstApprovedKey] = serverTimestamp();
      updates[`${flagPrefix}ReportFirstApprovedBy`] = user?.uid ?? null;
      updates[`${flagPrefix}ReportFirstApprovedByName`] = user?.displayName ?? null;
    }

    transaction.update(docRef, updates);

    result = {
      isFullyApproved,
      outstandingCodes: [...outstandingCodes],
      wasFirstApproval,
    };
  });

  return result;
};
